// src/components/ui/NotificationBell.tsx
// Bell icon + unread badge + dropdown list, styled to match the existing
// icon-only Sidebar nav (see IconButton in components/layout/Sidebar.tsx).
// Lives in the persistent app shell so it's visible from every dashboard
// screen — the notification list itself comes from NotificationContext's
// SSE connection, this component only renders it.
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { brand, fonts } from '@/lib/tokens';
import { useNotifications, type LiveNotification } from '@/context/NotificationContext';

function timeAgo(ts: number): string {
  const seconds = Math.max(0, Math.floor((Date.now() - ts) / 1000));
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

const ICON_FOR_TYPE: Record<LiveNotification['type'], string> = {
  'booking.ready_for_review': 'fact_check',
  'email.received': 'mail',
  'thread_reply.received': 'reply',
  'workflow.failed': 'error',
  'workflow.quarantined': 'gpp_maybe',
  'substitution.required': 'swap_horiz',
  'audit.alert': 'policy',
};

export function NotificationBell() {
  const { notifications, unreadCount, isConnected, markAllRead, dismiss } = useNotifications();
  const [isOpen, setIsOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  // The live SSE connection can silently drop without the operator ever
  // knowing — no error, no toast, just no more real-time updates. Give a
  // brief grace period before showing the dot so the very first render
  // (isConnected starts false until the EventSource's onopen fires) doesn't
  // flash it on every page load.
  const [showDisconnected, setShowDisconnected] = useState(false);
  useEffect(() => {
    if (isConnected) {
      setShowDisconnected(false);
      return;
    }
    const timer = window.setTimeout(() => setShowDisconnected(true), 4000);
    return () => window.clearTimeout(timer);
  }, [isConnected]);

  useEffect(() => {
    if (!isOpen) return;
    const onPointerDown = (e: PointerEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen]);

  const toggle = () => {
    setIsOpen((prev) => {
      const next = !prev;
      if (next) markAllRead();
      return next;
    });
  };

  return (
    <div ref={rootRef} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={toggle}
        aria-label={
          showDisconnected
            ? 'Notifications (live updates disconnected)'
            : unreadCount > 0
              ? `Notifications (${unreadCount} unread)`
              : 'Notifications'
        }
        aria-expanded={isOpen}
        title={showDisconnected ? 'Live updates disconnected — reload to reconnect' : 'Notifications'}
        className="sidebar-icon-btn"
        style={{
          width: 40,
          height: 40,
          borderRadius: 12,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          border: 'none',
          background: 'transparent',
          position: 'relative',
        }}
      >
        <span className="material-symbols-outlined" style={{ fontSize: 20 }} aria-hidden>
          notifications
        </span>
        {unreadCount > 0 && (
          <span
            aria-hidden
            style={{
              position: 'absolute',
              top: 4,
              right: 4,
              minWidth: 16,
              height: 16,
              padding: '0 3px',
              borderRadius: 8,
              background: brand.error,
              color: brand.canvas,
              fontFamily: fonts.mono,
              fontSize: 9,
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              lineHeight: 1,
            }}
          >
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
        {showDisconnected && (
          <span
            aria-hidden
            style={{
              position: 'absolute',
              bottom: 5,
              right: 5,
              width: 8,
              height: 8,
              borderRadius: '50%',
              background: brand.onSurfaceVariant,
              border: `1.5px solid ${brand.canvas}`,
            }}
          />
        )}
      </button>

      {isOpen && (
        <div
          role="menu"
          style={{
            position: 'absolute',
            left: '100%',
            bottom: 0,
            marginLeft: 10,
            width: 320,
            maxHeight: 420,
            overflowY: 'auto',
            // Was hardcoded rgba(11,11,11,0.96) — always a near-black panel
            // even in light mode, clashing with the rest of the light-themed
            // dashboard around it. globals.css already defines a dedicated,
            // per-theme "glass panel" token for exactly this pattern — use
            // that instead of a one-off opacity value.
            background: 'var(--surface-glass-strong)',
            backdropFilter: 'blur(24px)',
            WebkitBackdropFilter: 'blur(24px)',
            border: `1px solid ${brand.hairline}`,
            borderRadius: 12,
            boxShadow: '0 8px 30px rgba(0,0,0,0.5)',
            zIndex: 90,
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '12px 14px',
              borderBottom: `1px solid ${brand.hairlineSoft}`,
            }}
          >
            <span style={{ fontFamily: fonts.sans, fontSize: 13, fontWeight: 600, color: brand.onSurface }}>
              Notifications
            </span>
            {showDisconnected && (
              <span style={{ fontFamily: fonts.mono, fontSize: 10, color: brand.onSurfaceVariant }}>
                Live updates offline
              </span>
            )}
          </div>

          {notifications.length === 0 ? (
            <p
              style={{
                fontFamily: fonts.sans,
                fontSize: 12,
                color: brand.onSurfaceVariant,
                padding: '20px 14px',
                textAlign: 'center',
                margin: 0,
              }}
            >
              Nothing yet — new booking activity will show up here.
            </p>
          ) : (
            <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
              {notifications.map((n) => (
                <li key={n.id}>
                  <Link
                    // Was `?workflow=` — DashboardPage's ?booking= URL-restore
                    // effect (and everything that reads searchParams for the
                    // open booking) only ever looks for `?booking=`, so this
                    // deep link silently did nothing beyond navigating to the
                    // bare inbound queue. workflowId and bookingId are the
                    // same identifier throughout this app; fixed 28 Sep 2026.
                    to={n.workflowId ? `/dashboard?booking=${n.workflowId}` : '/dashboard'}
                    onClick={() => {
                      // Dismiss (remove), not just mark read — clicking a
                      // notification to act on it should make it disappear
                      // from the list, not just dim it. Fixed 28 Sep 2026.
                      dismiss(n.id);
                      setIsOpen(false);
                    }}
                    style={{
                      display: 'flex',
                      gap: 10,
                      padding: '10px 14px',
                      borderBottom: `1px solid ${brand.hairlineSoft}`,
                      textDecoration: 'none',
                      background: n.read ? 'transparent' : brand.accentFaint,
                    }}
                  >
                    <span
                      className="material-symbols-outlined"
                      aria-hidden
                      style={{ fontSize: 18, color: brand.accent, flexShrink: 0, marginTop: 1 }}
                    >
                      {ICON_FOR_TYPE[n.type]}
                    </span>
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span
                        style={{
                          display: 'block',
                          fontFamily: fonts.sans,
                          fontSize: 12,
                          fontWeight: 600,
                          color: brand.onSurface,
                        }}
                      >
                        {n.title}
                      </span>
                      <span
                        style={{
                          display: 'block',
                          fontFamily: fonts.sans,
                          fontSize: 12,
                          color: brand.onSurfaceVariant,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {n.body}
                      </span>
                      <span style={{ display: 'block', fontFamily: fonts.mono, fontSize: 10, color: brand.monoDim, marginTop: 2 }}>
                        {timeAgo(n.receivedAt)}
                      </span>
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
